import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';

import { Button } from '@/ui/Button';
import { Icon } from '@/ui/Icon';
import { FormRecordingVideoPreview } from '@/administration/pages/Journey/common/modals/LiveEventModal/LiveEventForm/fields/FormRecording/FormRecordingVideoPreview';
import { UploadedFileVideo } from '@/component/FileUpload/types';
import { formatSingleDateInUserTimezone } from '@/utils/dates';
import { useCompleteLiveEvent } from './hooks/useCompleteLiveEvent';

const SectionStyled = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TitleCaptionStyled = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleContainerStyled = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
`;

const TitleStyled = styled.span`
  ${({ theme }) => theme.typography.title3}
  color: ${({ theme }) => theme.colors.neutral.onBackground};
`;

const CaptionContainerStyled = styled.p`
  width: 100%;
  display: flex;
  gap: 8px;
`;

const CaptionStyled = styled.span`
  ${({ theme }) => theme.typography.body3}
  color: ${({ theme }) => theme.colors.neutral.outline2};
`;

const VideoStyled = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const VideoBlockStyled = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const CompletionStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
`;

const CompletionMessageContainerStyled = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 8px;
`;

const CompletionIconStyled = styled.span`
  font-weight: 400;
  font-size: 20px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  vertical-align: middle;
  color: ${({ theme }) => theme.colors.neutral.onBackground};
  padding: 4px;
`;

const CompletionMessageTextStyled = styled.div`
  ${({ theme }) => theme.typography.subheadline};
  color: ${({ theme }) => theme.colors.neutral.onBackground};
`;

type LiveEventRecordingSectionProps = {
  recordingURL: string;
  hasEnded: boolean;
  registration?: string;

  progress?: {
    completed: boolean;
    completedPercentage: number;
  };
  completedAt?: Date | null;
};

const isVimeoUrl = (url: string): boolean => {
  return url.includes('vimeo.com');
};

const extractVimeoId = (url: string): string | null => {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : null;
};

export const LiveEventRecordingSection = ({
  recordingURL,
  hasEnded,
  progress,
  registration,
  completedAt,
}: LiveEventRecordingSectionProps): JSX.Element | null => {
  const { id: liveEventId } = useParams<{ id: string }>();

  const initialCompletedAt = completedAt
    ? typeof completedAt === 'string'
      ? completedAt
      : new Date(completedAt).toISOString()
    : null;
  const initialIsCompleted = progress?.completed ?? false;

  const [localCompletedAt, setLocalCompletedAt] = useState<string | null>(initialCompletedAt);
  const [localIsCompleted, setLocalIsCompleted] = useState<boolean>(initialIsCompleted);

  const { completeAndRefetch, loading: markCompletionLoading } = useCompleteLiveEvent({
    liveEventId: liveEventId || '',
    registration: registration || '',
  });

  const handleMarkAsCompleted = async () => {
    const completionDate = await completeAndRefetch();

    setLocalIsCompleted(true);
    setLocalCompletedAt(completionDate);
  };

  if (!hasEnded || !recordingURL) {
    return null;
  }

  const isCompleted = localIsCompleted;
  const canMarkAsCompleted = !isCompleted;

  const videoLink = !isVimeoUrl(recordingURL) ? recordingURL : undefined;
  const uploadedFileVideo: UploadedFileVideo | undefined = isVimeoUrl(recordingURL)
    ? {
        vimeoId: extractVimeoId(recordingURL) || '',
        ratio: 0.5625,
        duration: undefined,
        thumbnail: undefined,
      }
    : undefined;

  const completionMessage = localCompletedAt ? `Completed on ${formatSingleDateInUserTimezone(localCompletedAt)}` : 'Completed';
  return (
    <SectionStyled>
      <TitleCaptionStyled>
        <TitleContainerStyled>
          <TitleStyled>Recording</TitleStyled>
        </TitleContainerStyled>
        {!isCompleted && (
          <CaptionContainerStyled>
            <CaptionStyled>You can complete this item by watching the recorded session below.</CaptionStyled>
          </CaptionContainerStyled>
        )}
      </TitleCaptionStyled>
      <VideoStyled>
        <VideoBlockStyled>
          <FormRecordingVideoPreview
            videoLink={videoLink}
            uploadedFileVideo={uploadedFileVideo}
            borderTopRadius="12px"
            borderBottomRadius="12px"
          />
        </VideoBlockStyled>
      </VideoStyled>
      <CompletionStyled>
        {isCompleted ? (
          <CompletionMessageContainerStyled>
            <CompletionIconStyled>
              <Icon icon={['far', 'circle-check']} />
            </CompletionIconStyled>
            <CompletionMessageTextStyled>{completionMessage}</CompletionMessageTextStyled>
          </CompletionMessageContainerStyled>
        ) : (
          <Button
            variant="primary"
            icon="check"
            onClick={handleMarkAsCompleted}
            $loading={markCompletionLoading}
            disabled={markCompletionLoading || !canMarkAsCompleted}
            size="large"
          >
            Mark as completed
          </Button>
        )}
      </CompletionStyled>
    </SectionStyled>
  );
};
